import mitt from 'mitt';
import { createApp } from 'vue';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import { createVfm } from 'vue-final-modal';
import VueFileAgentNext from '@boindil/vue-file-agent-next';
import Vue3Toastify, { toast } from 'vue3-toastify';
import vClickOutside from 'v-click-outside';
import { datadogRum } from '@datadog/browser-rum';
import { ApolloClients } from '@vue/apollo-composable';
import FloatingVue, { vTooltip, vClosePopper } from 'floating-vue';
import loadModules from '@modules';
import registerIcons from '@/icons';
import { auth } from '@/directives/auth';
import { scrollY, scrollX } from '@sellia/base/directives/scroll';
import { createDeviceDetector } from '@/utils/device.detector';
import { apolloAnalyticsClient, apolloClient, createProvider } from './vue-apollo';
import registerValidations from './validations';
import App from './App.vue';
import router from './router';
import store from './store';
import { i18n } from './i18n';
import mixins from './mixins';
import filters from './utils/filters';
import { initializePinia } from './stores';



const routerApp = loadModules(router, i18n);
const vfm = createVfm();
const emitter = mitt();
const app = createApp(App);
const pinia = initializePinia();

const { bind, unbind } = vClickOutside.directive;
app.directive('click-outside', {
  mounted(el, bindling) {
    bind(el, { value: bindling.value });
  },
  beforeUnmount(el) {
    unbind(el);
  },
});

// Config
if (import.meta.env.VITE_DD_ENVIRONMENT === 'develop') {
  app.config.devtools = true;
}
app.config.productionTip = import.meta.env.VITE_PRODUCTIONTIP || false;
app.config.globalProperties.emitter = emitter;

// Directives
FloatingVue.options.themes.tooltip.hideTriggers = events => [...events];
app.directive('auth', auth);
app.directive('scroll-y', scrollY);
app.directive('scroll-x', scrollX);
app.directive('tooltip', vTooltip);
app.directive('close-popper', vClosePopper);
app.use(store);
app.use(pinia);
app.use(createProvider());
app.provide(ApolloClients, {
  default: apolloClient,
  analytics: apolloAnalyticsClient,
});


// Plugins


app.use(routerApp);
app.use(i18n);
app.use(filters);
app.use(vfm);
app.use(VueFileAgentNext);
app.use(createDeviceDetector());


app.mixin(mixins);
registerIcons(app);
registerValidations();

// Google Maps
app.use(VueGoogleMaps, {
  load: {
    key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: 'places',
  },
});

const toastOptions = {
  autoClose: 5000,
  theme: 'colored',
  pauseOnHover: true,
  clearOnUrlChange: false,
  style: {
    zIndex: 9999,
  }
};
app.use(Vue3Toastify, toastOptions);
app.config.globalProperties.$toasted = {
  global: {
    info: (message) => toast.info(
      message, { ...toastOptions }
    ),
    success: (message) => toast.success(
      message, { ...toastOptions }
    ),
    error: (message) => toast.error(
      message, { ...toastOptions }
    ),
  }
};

const isProduction = import.meta.env.VITE_ADMIN_DD_ENVIRONMENT === 'production';

if (isProduction) {
  datadogRum.init({
    applicationId: import.meta.env.VITE_ADMIN_DD_APPLICATIONID,
    clientToken: import.meta.env.VITE_ADMIN_DD_CLIENTTOKEN,
    site: 'datadoghq.com',
    service: import.meta.env.VITE_ADMIN_DD_SERVICE,
    env: import.meta.env.VITE_ADMIN_DD_ENVIRONMENT,
    version: import.meta.env.PACKAGE_VERSION,
    defaultPrivacyLevel: 'mask-user-input',
    forwardErrorsToLogs: true,
    sessionSampleRate: 10,
    sessionReplaySampleRate: 10,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
  });
}

app.mount('#app');
